import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Column headers that stay visible while the *page* scrolls.
 *
 * List tables scroll vertically with the page — there is no second scrollbar
 * inside the table — but they still scroll horizontally inside their own box.
 * Those two wants conflict in CSS: a box with `overflow-x: auto` is a scroll
 * container on both axes (a `visible` overflow-y computes to `auto` next to a
 * non-visible overflow-x), and `position: sticky` pins to the nearest scroll
 * container. So a `<thead>` inside the horizontal scroller can only ever stick
 * to that box, never to the window.
 *
 * The way out is to render the header row a second time *outside* the
 * scroller, in a `position: sticky` bar that pins to the window, and drive it
 * from measurements of the real one:
 *
 *   - `widths` / `tableWidth` come from the live `<th>` boxes, so the copy
 *     lines up with the real columns (it renders with `table-layout: fixed`);
 *   - the bar is translated by the scroller's `scrollLeft`, so scrolling the
 *     table sideways carries the pinned header with it;
 *   - it is shown only once the real header has left the top of the viewport,
 *     so at rest there is exactly one header on screen.
 *
 * The copy is a re-render of the same JSX, not a DOM clone — sort buttons and
 * filter triggers keep working because they are the same React elements with
 * the same handlers.
 *
 * @param {Array} deps Values that change the table's shape (visible column
 *                     keys, row count…) and therefore require re-measuring.
 * @param {number} offset Distance from the top of the window the bar pins at —
 *                        the app header sits there. Must match the `top` in
 *                        CSS, or the copy appears before the real header is
 *                        actually covered.
 * @returns {{scrollRef, headRef, innerRef, active, widths, tableWidth, headHeight}}
 */
export const STICKY_HEADER_OFFSET = 64; // $header-height

export default function useStickyTableHeader(deps = [], offset = STICKY_HEADER_OFFSET) {
  const scrollRef = useRef(null);   // the horizontal scroller
  const headRef = useRef(null);     // the real <thead>
  const innerRef = useRef(null);    // the translated copy
  const barRef = useRef(null);      // the sticky horizontal scrollbar
  const [active, setActive] = useState(false);
  const [metrics, setMetrics] = useState({
    widths: [], tableWidth: 0, headHeight: 0, overflowing: false,
  });

  const measure = useCallback(() => {
    const head = headRef.current;
    const scroller = scrollRef.current;
    if (!head) return;
    const table = head.closest('table');
    const widths = Array.from(head.querySelectorAll('th')).map((th) => th.getBoundingClientRect().width);
    const tableWidth = table ? table.getBoundingClientRect().width : 0;
    const headHeight = head.getBoundingClientRect().height;
    const overflowing = !!scroller && scroller.scrollWidth > scroller.clientWidth + 1;

    // Bail out when nothing moved — this runs from a ResizeObserver that also
    // fires on every row added, and a new object each time would re-render the
    // whole grid for no reason.
    setMetrics((prev) => {
      const same = prev.tableWidth === tableWidth
        && prev.headHeight === headHeight
        && prev.overflowing === overflowing
        && prev.widths.length === widths.length
        && prev.widths.every((w, i) => Math.abs(w - widths[i]) < 0.5);
      return same ? prev : { widths, tableWidth, headHeight, overflowing };
    });
  }, []);

  // Re-measure whenever the table's shape can have changed: its own box
  // (columns toggled, rows loaded) or the space it lives in (window resize).
  useLayoutEffect(() => {
    measure();
    const table = headRef.current?.closest('table');
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      if (table) observer.observe(table);
      if (scrollRef.current) observer.observe(scrollRef.current);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
    // eslint-disable-next-line
  }, [measure, ...deps]);

  // Pin only once the real header has gone under the app header, so the two
  // are never on screen together. The root is inset by `offset` so "covered"
  // means covered by the app header, not merely off the top of the window.
  useLayoutEffect(() => {
    const head = headRef.current;
    if (!head || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setActive(!entry.isIntersecting && entry.boundingClientRect.top < offset),
      { threshold: 0, rootMargin: `-${offset}px 0px 0px 0px` }
    );
    observer.observe(head);
    return () => observer.disconnect();
    // eslint-disable-next-line
  }, [offset, ...deps]);

  // Keep the three horizontal positions in step: the table, the pinned header
  // above it, and the sticky scrollbar below it. Written straight to the nodes
  // — this fires per scroll frame and must not re-render.
  //
  // The table's own scrollbar is hidden in CSS and the sticky bar is the one
  // the user drags, so the two never both appear at the bottom of the table.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return undefined;
    let frame = 0;
    let source = null; // guards against the two scrollers echoing each other

    const paint = () => {
      frame = 0;
      if (innerRef.current) {
        innerRef.current.style.transform = `translateX(${-scroller.scrollLeft}px)`;
      }
      if (barRef.current && source !== 'bar' && barRef.current.scrollLeft !== scroller.scrollLeft) {
        barRef.current.scrollLeft = scroller.scrollLeft;
      }
      source = null;
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(paint); };

    const onTableScroll = () => { if (source !== 'bar') source = 'table'; schedule(); };
    const onBarScroll = () => {
      if (source === 'table') return;
      source = 'bar';
      scroller.scrollLeft = barRef.current.scrollLeft;
      schedule();
    };

    scroller.addEventListener('scroll', onTableScroll, { passive: true });
    const bar = barRef.current;
    bar?.addEventListener('scroll', onBarScroll, { passive: true });
    paint();
    return () => {
      scroller.removeEventListener('scroll', onTableScroll);
      bar?.removeEventListener('scroll', onBarScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [active, metrics.tableWidth, metrics.overflowing]);

  return {
    scrollRef,
    headRef,
    innerRef,
    barRef,
    active,
    widths: metrics.widths,
    tableWidth: metrics.tableWidth,
    headHeight: metrics.headHeight,
    overflowing: metrics.overflowing,
  };
}
